import {
  apiErrorSchema,
  accessRequestSchema,
  accessRequestResponseSchema,
  type AccessRequest,
  type AccessRequestResponse,
  serverConnectionSchema,
  serverConnectionRequestSchema,
  addSpatialSubspaceRequestSchema,
  listSpatialSubspacesQuerySchema,
  spatialSubspacesResponseSchema,
  recoverServerRequestSchema,
  recoverServerResponseSchema,
  type RecoverServerRequest,
  type RecoverServerResponse,
  provisionServerRequestSchema,
  serverMonitoringQuerySchema,
  type ServerConnectionRequest,
  type ProvisionServerRequest,
  type ServerMonitoringQuery,
  serverMonitoringSchema,
  provisionServerResponseSchema,
  deleteServerRequestSchema,
  deleteServerResponseSchema,
  type ServerConnection,
  type ServerMonitoring,
  type AddSpatialSubspaceRequest,
  type ListSpatialSubspacesQuery,
  type SpatialSubspacesResponse,
  type ProvisionServerResponse,
  type DeleteServerRequest,
  type DeleteServerResponse,
  accountCapacitySchema,
  type AccountCapacity,
  adminNetworkTelemetrySchema,
  type AdminNetworkTelemetry,
  createGlobalNetworkResponseSchema,
  createServerRequestSchema,
  createServerResponseSchema,
  updateServerCapacityRequestSchema,
  updateServerCapacityResponseSchema,
  type UpdateServerCapacityRequest,
  type UpdateServerCapacityResponse,
  getGlobalNetworkResponseSchema,
  listServersResponseSchema,
  listLogsQuerySchema,
  listLogsResponseSchema,
  type ListLogsQuery,
  type ListLogsResponse,
  meResponseSchema,
  deleteAccountRequestSchema,
  deleteAccountResponseSchema,
  type CreateGlobalNetworkResponse,
  type CreateServerRequest,
  type CreateServerResponse,
  type GetGlobalNetworkResponse,
  type ListServersResponse,
  type MeResponse,
} from "@woven/contracts";
import type { z } from "zod";
import type { WebEnv } from "./env";

export class ControlApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = "ControlApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

export interface ControlApiClient {
  getNetworkTelemetry(signal?: AbortSignal): Promise<AdminNetworkTelemetry>;
  getMe(signal?: AbortSignal): Promise<MeResponse>;
  requestAccess(input: AccessRequest, signal?: AbortSignal): Promise<AccessRequestResponse>;
  notifySignup(signal?: AbortSignal): Promise<void>;
  getAccountCapacity(signal?: AbortSignal): Promise<AccountCapacity>;
  deleteAccount(
    input: z.input<typeof deleteAccountRequestSchema>
  ): Promise<z.output<typeof deleteAccountResponseSchema>>;
  listServers(signal?: AbortSignal): Promise<ListServersResponse>;
  provisionServer(
    id: string,
    input: ProvisionServerRequest,
    signal?: AbortSignal
  ): Promise<ProvisionServerResponse>;
  recoverServer(
    id: string,
    input: RecoverServerRequest,
    signal?: AbortSignal
  ): Promise<RecoverServerResponse>;
  getServerConnection(
    id: string,
    input: ServerConnectionRequest,
    signal?: AbortSignal
  ): Promise<ServerConnection>;
  getServerMonitoring(
    id: string,
    query: ServerMonitoringQuery,
    signal?: AbortSignal
  ): Promise<ServerMonitoring>;
  listServerSubspaces(
    id: string,
    query: ListSpatialSubspacesQuery,
    signal?: AbortSignal
  ): Promise<SpatialSubspacesResponse>;
  addServerSubspace(
    id: string,
    input: AddSpatialSubspaceRequest,
    signal?: AbortSignal
  ): Promise<SpatialSubspacesResponse>;
  deleteServer(
    id: string,
    input: DeleteServerRequest,
    signal?: AbortSignal
  ): Promise<DeleteServerResponse>;
  recoverServerDeletion(
    id: string,
    input: DeleteServerRequest,
    signal?: AbortSignal
  ): Promise<DeleteServerResponse>;
  updateServerCapacity(
    id: string,
    input: UpdateServerCapacityRequest,
    signal?: AbortSignal
  ): Promise<UpdateServerCapacityResponse>;
  listLogs(query?: ListLogsQuery, signal?: AbortSignal): Promise<ListLogsResponse>;
  createServer(input: CreateServerRequest, signal?: AbortSignal): Promise<CreateServerResponse>;
  getGlobalNetwork(signal?: AbortSignal): Promise<GetGlobalNetworkResponse>;
  createGlobalNetwork(): Promise<CreateGlobalNetworkResponse>;
}

type TokenProvider = () => Promise<string | null>;

// App Check is monitor-only during the current rollout, so optional browser
// attestation must not indefinitely block mandatory Firebase authentication.
const APP_CHECK_WAIT_MS = 2_000;
const APP_CHECK_RETRY_COOLDOWN_MS = 5 * 60_000;

/**
 * Browser client for the Woven control API.
 *
 * In local test mode (`AUTH_MODE=test` on the API) it sends the dev token and
 * App Check passphrase from Vite env; otherwise it uses the real Firebase ID
 * token. Tokens travel only in headers and are never logged here.
 */
export function createControlApiClient(
  env: WebEnv,
  getAccessToken: TokenProvider,
  getAppCheckToken?: TokenProvider,
  assertSession?: () => void
): ControlApiClient {
  const baseUrl = env.controlApiUrl.replace(/\/+$/, "");
  let appCheckInFlight: Promise<string | null> | null = null;
  let appCheckRetryAfter = 0;

  function optionalAppCheckToken(): Promise<string | null> {
    if (!getAppCheckToken || Date.now() < appCheckRetryAfter) return Promise.resolve(null);
    if (appCheckInFlight) return appCheckInFlight;

    const pending = Promise.resolve()
      .then(getAppCheckToken)
      .then(
        (token) => {
          appCheckRetryAfter = 0;
          return token;
        },
        () => {
          appCheckRetryAfter = Date.now() + APP_CHECK_RETRY_COOLDOWN_MS;
          return null;
        }
      );
    appCheckInFlight = pending;
    void pending.finally(() => {
      if (appCheckInFlight === pending) appCheckInFlight = null;
    });
    return pending;
  }

  async function request(
    path: string,
    init: { method?: string; body?: unknown; signal?: AbortSignal; cache?: RequestCache } = {}
  ): Promise<unknown> {
    init.signal?.throwIfAborted();
    assertSession?.();
    const controller = new AbortController();
    const cancel = () => controller.abort(new DOMException("Request cancelled", "AbortError"));
    init.signal?.addEventListener("abort", cancel, { once: true });
    let timedOut = false;
    let appCheckTimeout: ReturnType<typeof setTimeout> | undefined;
    const timeout = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, 15_000);
    let rejectAbort: () => void = () => {};
    const aborted = new Promise<never>((_resolve, reject) => {
      rejectAbort = () => reject(controller.signal.reason);
      controller.signal.addEventListener("abort", rejectAbort, { once: true });
    });
    let response: Response;
    let payload: unknown;
    let requestStarted = false;
    try {
      const headers = new Headers();
      headers.set("Accept", "application/json");

      const accessToken = env.controlApiToken
        ? Promise.resolve(env.controlApiToken)
        : Promise.resolve().then(getAccessToken);
      const sdkAppCheckToken = optionalAppCheckToken();
      const appCheckDeadline = new Promise<null>((resolve) => {
        appCheckTimeout = setTimeout(() => {
          appCheckRetryAfter = Math.max(
            appCheckRetryAfter,
            Date.now() + APP_CHECK_RETRY_COOLDOWN_MS
          );
          resolve(null);
        }, APP_CHECK_WAIT_MS);
      });
      const [token, resolvedAppCheckToken] = await Promise.all([
        Promise.race([accessToken, aborted]),
        Promise.race([sdkAppCheckToken, appCheckDeadline, aborted]),
      ]);
      const appCheckToken = resolvedAppCheckToken ?? env.controlApiAppCheckToken ?? null;
      if (appCheckToken) headers.set("X-Firebase-AppCheck", appCheckToken);
      if (token) headers.set("Authorization", `Bearer ${token}`);

      const body = init.body === undefined ? undefined : JSON.stringify(init.body);
      if (body !== undefined) {
        headers.set("Content-Type", "application/json");
      }

      controller.signal.throwIfAborted();
      assertSession?.();
      requestStarted = true;
      response = await Promise.race([
        fetch(`${baseUrl}${path}`, {
          method: init.method ?? "GET",
          headers,
          body,
          signal: controller.signal,
          cache: init.cache,
        }),
        aborted,
      ]);
      payload = await Promise.race([response.json().catch(() => null), aborted]);
      controller.signal.throwIfAborted();
    } catch (error) {
      if (controller.signal.aborted && !timedOut) {
        throw controller.signal.reason;
      }
      if (timedOut) {
        throw new ControlApiError(
          0,
          "REQUEST_TIMEOUT",
          "The Control API did not respond within 15 seconds. Check API availability and your connection. A submitted operation may still complete; check its status before retrying."
        );
      }
      if (!requestStarted) throw error;
      throw new ControlApiError(
        0,
        "NETWORK_UNAVAILABLE",
        "Unable to reach the Control API. Check that the API is running and the configured API address is correct. A connection or CORS issue may also prevent access."
      );
    } finally {
      clearTimeout(timeout);
      if (appCheckTimeout) clearTimeout(appCheckTimeout);
      init.signal?.removeEventListener("abort", cancel);
      controller.signal.removeEventListener("abort", rejectAbort);
    }
    assertSession?.();
    if (!response.ok) {
      const parsed = apiErrorSchema.safeParse(payload);
      if (parsed.success) {
        throw new ControlApiError(
          response.status,
          parsed.data.error.code,
          parsed.data.error.message,
          parsed.data.error.details
        );
      }
      throw new ControlApiError(
        response.status,
        "INTERNAL",
        "control-api returned an unparseable error"
      );
    }
    return payload;
  }

  async function serverRequest<T>(
    id: string,
    action: string,
    schema: z.ZodType<T>,
    init: { method?: string; body?: unknown; signal?: AbortSignal }
  ): Promise<T> {
    try {
      const payload = await request(`/v1/servers/${encodeURIComponent(id)}/${action}`, {
        ...init,
        cache: "no-store",
      });
      const parsed = schema.safeParse(payload);
      if (!parsed.success)
        throw new ControlApiError(0, "INVALID_RESPONSE", "Invalid server response.");
      return parsed.data;
    } catch (error) {
      if (init.signal?.aborted) throw init.signal.reason;
      if (error instanceof ControlApiError) {
        const messages: Partial<Record<string, string>> = {
          RECENT_AUTH_REQUIRED:
            "Recent authentication is required before connection details can be revealed.",
          UNAUTHENTICATED: "The current authentication session is not valid.",
          PERMISSION_DENIED: "The current account does not have permission for this server action.",
          UNAVAILABLE:
            "Managed provisioning or the Woven node is unavailable. Ask the operator to enable or restore the integration, then retry.",
        };
        const message = messages[error.code];
        if (message) {
          throw new ControlApiError(error.status, error.code, message);
        }
      }
      throw new ControlApiError(
        error instanceof ControlApiError ? error.status : 0,
        "SERVER_OPERATION_FAILED",
        "The server operation could not be confirmed. Refresh server status before retrying; a submitted operation may still complete."
      );
    }
  }

  return {
    async provisionServer(id, input, signal) {
      return serverRequest(id, "provision", provisionServerResponseSchema, {
        method: "POST",
        body: provisionServerRequestSchema.parse(input),
        signal,
      });
    },
    async recoverServer(id, input, signal) {
      return serverRequest(id, "recover", recoverServerResponseSchema, {
        method: "POST",
        body: recoverServerRequestSchema.parse(input),
        signal,
      });
    },
    async getServerConnection(id, input, signal) {
      return serverRequest(id, "connection", serverConnectionSchema, {
        method: "POST",
        body: serverConnectionRequestSchema.parse(input),
        signal,
      });
    },
    async getServerMonitoring(id, query, signal) {
      const parsed = serverMonitoringQuerySchema.parse(query);
      const params = new URLSearchParams(parsed);
      return serverRequest(id, `monitoring?${params}`, serverMonitoringSchema, { signal });
    },
    async listServerSubspaces(id, query, signal) {
      const parsed = listSpatialSubspacesQuerySchema.parse(query);
      const params = new URLSearchParams(parsed);
      return serverRequest(id, `subspaces?${params}`, spatialSubspacesResponseSchema, { signal });
    },
    async addServerSubspace(id, input, signal) {
      return serverRequest(id, "subspaces", spatialSubspacesResponseSchema, {
        method: "POST",
        body: addSpatialSubspaceRequestSchema.parse(input),
        signal,
      });
    },
    async updateServerCapacity(id, input, signal) {
      return serverRequest(id, "capacity", updateServerCapacityResponseSchema, {
        method: "PATCH",
        body: updateServerCapacityRequestSchema.parse(input),
        signal,
      });
    },
    async deleteServer(id, input, signal) {
      return serverRequest(id, "delete", deleteServerResponseSchema, {
        method: "POST",
        body: deleteServerRequestSchema.parse(input),
        signal,
      });
    },
    async recoverServerDeletion(id, input, signal) {
      return serverRequest(id, "delete-recovery", deleteServerResponseSchema, {
        method: "POST",
        body: deleteServerRequestSchema.parse(input),
        signal,
      });
    },
    async getNetworkTelemetry(signal) {
      return adminNetworkTelemetrySchema.parse(await request("/v1/network/telemetry", { signal }));
    },
    async getMe(signal) {
      const data = await request("/v1/me", { cache: "no-store", signal });
      return meResponseSchema.parse(data);
    },
    async requestAccess(input, signal) {
      const body = accessRequestSchema.parse(input);
      const data = await request("/v1/me/access-request", {
        method: "POST",
        body,
        signal,
        cache: "no-store",
      });
      return accessRequestResponseSchema.parse(data);
    },
    async notifySignup(signal) {
      await request("/v1/me/signup", { method: "POST", signal, cache: "no-store" });
    },
    async getAccountCapacity(signal) {
      return accountCapacitySchema.parse(
        await request("/v1/me/capacity", { signal, cache: "no-store" })
      );
    },
    async deleteAccount(input) {
      const body = deleteAccountRequestSchema.parse(input);
      const data = await request("/v1/me/delete", { method: "POST", body });
      return deleteAccountResponseSchema.parse(data);
    },
    async listLogs(query = {}, signal) {
      const parsed = listLogsQuerySchema.parse(query);
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(parsed)) {
        if (value !== undefined) params.set(key, String(value));
      }
      return listLogsResponseSchema.parse(await request(`/v1/logs?${params}`, { signal }));
    },
    async listServers(signal) {
      const data = await request("/v1/servers", { signal, cache: "no-store" });
      return listServersResponseSchema.parse(data);
    },
    async createServer(input, signal) {
      const data = await request("/v1/servers", {
        method: "POST",
        body: createServerRequestSchema.parse(input),
        signal,
      });
      return createServerResponseSchema.parse(data);
    },
    async getGlobalNetwork(signal) {
      const data = await request("/v1/network/global", { signal });
      return getGlobalNetworkResponseSchema.parse(data);
    },
    async createGlobalNetwork() {
      const data = await request("/v1/network/global", { method: "POST" });
      return createGlobalNetworkResponseSchema.parse(data);
    },
  };
}
